"use client"

import { routing } from "@/i18n/routing"
import { ADMIN_SITE_CONFIG } from "@/constants/site-configs"
import { usePathname } from "next/navigation"
import { useCallback, useEffect, useRef, useState } from "react"
import styles from "./admin-bar.module.css"

interface AdminBarState {
  draft?: boolean
  editable?: boolean
  editUrl?: string
  /** true = đã xuất bản, false = bản nháp, undefined = không phải page/post cụ thể. */
  published?: boolean
}

const HIDE_KEY = "adminbar:hidden"
/** Cookie KHÔNG httpOnly, chỉ là cờ "phiên này có thể là admin" cho client. */
const HINT_COOKIE = "admin_hint="
/** Origin của CMS/admin (prod khác domain với public site). Trống → same-origin. */
const ADMIN_ORIGIN = (ADMIN_SITE_CONFIG.ADMIN_BASE_URL || "").replace(/\/$/, "")

/** Link về admin phải trỏ đúng origin CMS, không phải public origin đang xem. */
function getAdminHref(path: string): string {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`
  return `${ADMIN_ORIGIN}${normalizedPath}`
}

// Tách (locale, contentPath) từ pathname trình duyệt. Default locale (vi) không
// có prefix; en/cs có prefix `/en`, `/cs` (routing `as-needed`).
function splitLocale(pathname: string): { locale: string; path: string } {
  const segs = pathname.split("/").filter(Boolean)
  const first = segs[0]
  if (first && first !== routing.defaultLocale && (routing.locales as readonly string[]).includes(first)) {
    return { locale: first, path: segs.slice(1).join("/") }
  }
  return { locale: routing.defaultLocale, path: segs.join("/") }
}

function hasAdminHint(): boolean {
  if (typeof document === "undefined") return false
  return document.cookie.split(";").some(c => c.trim().startsWith(HINT_COOKIE))
}

/**
 * Admin-bar cho Draft Mode. Luôn được mount trong layout công khai nhưng:
 *  - Khách ẩn danh (không cookie `admin_hint`) → không fetch, render null.
 *    Nhờ vậy layout công khai KHÔNG đọc draftMode() ở server → ISR nguyên vẹn.
 *  - Admin → gọi `/api/admin-bar`; chỉ hiện bar khi Draft Mode đang bật, và chỉ
 *    hiện nút "Sửa nội dung" khi user đủ quyền + đúng site.
 *
 * Bar dùng `position: sticky` (nằm trong flow) nên tự chiếm một dải ở đỉnh và
 * KHÔNG đè lên nội dung. Chiều cao thật được publish ra biến CSS `--admin-bar-h`
 * để header sticky/fixed của site tự dời `top` xuống, tránh chồng lên bar.
 */
export default function AdminBar() {
  const pathname = usePathname()
  const [state, setState] = useState<AdminBarState | null>(null)
  const [loading, setLoading] = useState(false)
  const [hidden, setHidden] = useState(false)
  const [mounted, setMounted] = useState(false)
  const barRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    setHidden(sessionStorage.getItem(HIDE_KEY) === "1")
    setMounted(true)
  }, [])

  useEffect(() => {
    if (!hasAdminHint()) {
      setState(null)
      setLoading(false)
      return
    }

    // Xoá link sửa của trang CŨ ngay khi điều hướng, tránh bấm nhầm sang nội
    // dung trước đó trong lúc request mới chưa về.
    setState(prev => (prev ? { ...prev, editable: false, editUrl: undefined } : prev))
    setLoading(true)

    const controller = new AbortController()
    const { locale, path } = splitLocale(pathname || "/")
    const qs = new URLSearchParams({ path, locale }).toString()

    fetch(`/api/admin-bar?${qs}`, { cache: "no-store", signal: controller.signal })
      .then(res => (res.ok ? res.json() : null))
      .then((data: AdminBarState | null) => {
        setState(data)
        setLoading(false)
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setState(null)
          setLoading(false)
        }
      })

    return () => controller.abort()
  }, [pathname])

  const visible = mounted && !!state?.draft && !hidden

  // Đẩy chiều cao thật của bar ra biến CSS để header sticky của site tự né.
  useEffect(() => {
    const root = document.documentElement
    const el = barRef.current
    if (!visible || !el) {
      root.style.removeProperty("--admin-bar-h")
      return
    }
    const sync = () => root.style.setProperty("--admin-bar-h", `${el.offsetHeight}px`)
    sync()
    const ro = new ResizeObserver(sync)
    ro.observe(el)
    return () => {
      ro.disconnect()
      root.style.removeProperty("--admin-bar-h")
    }
  }, [visible])

  const hide = useCallback(() => {
    sessionStorage.setItem(HIDE_KEY, "1")
    setHidden(true)
  }, [])

  const show = useCallback(() => {
    sessionStorage.removeItem(HIDE_KEY)
    setHidden(false)
  }, [])

  // Trang /preview của UI Builder đã có thanh công cụ riêng (Back / Edit).
  // AdminBar (sticky, z-index cao) sẽ đè lên thanh đó nên ẩn hẳn ở đây.
  const isPreviewRoute = /(^|\/)preview(\/|$|\?)/.test(pathname || "")

  if (!mounted || !state?.draft || isPreviewRoute) return null

  // Chỉ bản nháp (publishedAt null) mới có nhãn trạng thái + nền nổi bật. Trang
  // đã xuất bản / không phải page-post cụ thể → nền trung tính, ô trái để trống.
  const isDraft = state.published === false
  const bg = isDraft ? "#7c4a03" : "#1f1f1f"

  if (hidden) {
    return (
      <button
        className={styles.desktopOnly}
        onClick={show}
        aria-label="Hiện thanh xem trước"
        style={{
          position: "fixed",
          top: 8,
          right: 8,
          // Trên mọi overlay toàn trang của site (vd. trang letak z-[9999]),
          // nếu không nút "hiện lại" bị che và bar không bật lại được.
          zIndex: 10000,
          width: 28,
          height: 28,
          display: "grid",
          placeItems: "center",
          border: "none",
          borderRadius: "50%",
          background: bg,
          color: "#fff",
          cursor: "pointer",
          boxShadow: "0 2px 8px rgb(0 0 0 / 0.3)",
        }}
      >
        <EyeIcon />
      </button>
    )
  }

  return (
    <div
      className={styles.desktopOnly}
      ref={barRef}
      role="region"
      aria-label="Thanh xem trước nội dung"
      style={{
        position: "sticky",
        top: 0,
        zIndex: 1000,
        background: bg,
        color: "#fff",
        fontSize: 13,
        padding: "7px 12px",
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        columnGap: 16,
        rowGap: 4,
      }}
    >
      {/* Ô trạng thái bên trái: LUÔN chừa chỗ (min-width cố định) để "Trang quản
          trị" đứng yên một vị trí dù có nhãn hay không. Chỉ bản nháp mới hiện chữ;
          trang public để trống. */}
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          minWidth: 90,
          flexShrink: 0,
          whiteSpace: "nowrap",
          fontWeight: 700,
        }}
      >
        {isDraft && (
          <>
            <EyeIcon />
            Bản nháp
          </>
        )}
      </span>

      <span style={{ ...actionGroupStyle, visibility: loading ? "hidden" : "visible" }}>
        <a href={getAdminHref("/admin")} style={linkStyle}>
          Trang quản trị
        </a>

        {state.editable && state.editUrl && (
          <a href={getAdminHref(state.editUrl)} style={actionLinkStyle}>
            <PencilIcon />
            Sửa nội dung này
          </a>
        )}

        <a
          href={`/api/draft/disable?redirect=${encodeURIComponent(pathname || "/")}`}
          style={actionLinkStyle}
        >
          <ExitIcon />
          Thoát xem trước
        </a>
      </span>

      <button
        onClick={hide}
        aria-label="Tạm ẩn thanh này"
        title="Tạm ẩn thanh (vẫn giữ chế độ xem trước; đăng xuất để thoát hẳn)"
        style={{
          marginLeft: "auto",
          display: "grid",
          placeItems: "center",
          width: 22,
          height: 22,
          padding: 0,
          border: "none",
          borderRadius: 4,
          background: "transparent",
          color: "#cfcfcf",
          cursor: "pointer",
        }}
      >
        <CloseIcon />
      </button>
    </div>
  )
}

const linkStyle: React.CSSProperties = {
  color: "#9ecbff",
  textDecoration: "none",
  whiteSpace: "nowrap",
}

const actionLinkStyle: React.CSSProperties = {
  ...linkStyle,
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  lineHeight: 1,
}

const actionGroupStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  flexWrap: "wrap",
  columnGap: 16,
  rowGap: 4,
}

const iconStyle: React.CSSProperties = {
  display: "block",
  flexShrink: 0,
}

function EyeIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      style={iconStyle}
      aria-hidden
    >
      <path d="M1 8s2.5-4.5 7-4.5S15 8 15 8s-2.5 4.5-7 4.5S1 8 1 8Z" />
      <circle cx="8" cy="8" r="2" />
    </svg>
  )
}

function CloseIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      style={iconStyle}
      aria-hidden
    >
      <path d="M4 4l8 8M12 4l-8 8" />
    </svg>
  )
}

function PencilIcon() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={iconStyle}
      aria-hidden
    >
      <path d="M10.8 2.2a1.4 1.4 0 0 1 2 2L5 12l-2.7.7L3 10l7.8-7.8Z" />
      <path d="m9.8 3.2 3 3" />
    </svg>
  )
}

function ExitIcon() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={iconStyle}
      aria-hidden
    >
      <path d="M6 2.5H3.5a1 1 0 0 0-1 1v9a1 1 0 0 0 1 1H6" />
      <path d="M10 5l3 3-3 3M13 8H6" />
    </svg>
  )
}
